import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import CircularProgress from "@mui/material/CircularProgress";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useTranslation } from "react-i18next";

import { DataGridPro, GridToolbar, GridOverlay } from "@mui/x-data-grid-pro";

import {
  Add as AddIcon,
  AssignmentOutlined as AssignmentIcon,
  CheckCircleOutline as CheckCircleIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  FilterAltOutlined as FilterIcon,
  HourglassEmptyOutlined as PendingIcon,
  Search as SearchIcon,
  WarningAmberOutlined as WarningIcon,
} from "@mui/icons-material";

import { useSelector } from "react-redux";

import AddComplaint from "./Modals/AddComplaint";
import ModifyComplaint from "./Modals/ModifyComplaint";
import { DeleteComplaintById } from "../../../Casserole/api/ComplaintApi";

import { FormatDateTimeAnneeMoisJour } from "../../../../utils/formateDate";

// ============================================================
// LOADING OVERLAY
// ============================================================

function CustomLoadingOverlay({ t }) {
  return (
    <GridOverlay>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={38} />

        <Typography variant="body2" color="text.secondary">
          {t("LoadingComplaints")}
        </Typography>
      </Box>
    </GridOverlay>
  );
}

// ============================================================
// NO ROWS OVERLAY
// ============================================================

function CustomNoRowsOverlay({ t }) {
  return (
    <GridOverlay>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <AssignmentIcon
          sx={{
            fontSize: 42,
            color: "text.disabled",
          }}
        />

        <Typography variant="body2" color="text.secondary">
          {t("NoComplaintsFound")}
        </Typography>
      </Box>
    </GridOverlay>
  );
}

// ============================================================
// COMPONENT
// ============================================================

function ComplainAdmin() {
  const userState = useSelector((state) => state.user);

  // ============================================================
  // TRANSLATION
  // ============================================================

  const { t } = useTranslation(["Complaint"]);

  // ============================================================
  // STATES
  // ============================================================

  const [complaints, setComplaints] = useState([]);

  const [selectedComplaint, setSelectedComplaint] = useState(null);

  const [openAddComplaint, setOpenAddComplaint] = useState(false);

  const [openModifyComplaint, setOpenModifyComplaint] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [statusFilter, setStatusFilter] = useState("ALL");

  const [loading, setLoading] = useState(true);

  // ============================================================
  // REFRESH
  // ============================================================

  const [refreshComplaints, setRefreshComplaints] = useState(0);

  // ============================================================
  // ALERT
  // ============================================================

  const [alertOpen, setAlertOpen] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");

  const [alertType, setAlertType] = useState("success");
  // ============================================================
  // DELETE DIALOG
  // ============================================================

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [complaintToDelete, setComplaintToDelete] = useState(null);

  // ============================================================
  // ROLES
  // ============================================================

  const hasAdminAccess = (roles = []) =>
    ["CasseroleAdmin", "AdminTI"].some((role) => roles.includes(role));

  const canModify = hasAdminAccess(userState.roles || []);

  // ============================================================
  // FETCH COMPLAINTS
  // ============================================================

  useEffect(() => {
    let isMounted = true;

    const fetchComplaints = async () => {
      setLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/Casserole/GetComplaints`,
          {
            method: "GET",

            credentials: "include",

            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userState.token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        const responseText = await response.text();

        let data;

        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error("JSON Parse Error:", parseError);

          throw new Error(t("InvalidJsonResponse"));
        }

        if (!Array.isArray(data)) {
          throw new Error(t("InvalidDataFormat"));
        }

        if (!isMounted) {
          return;
        }

        // ======================================================
        // FORMAT DATA
        // ======================================================

        const formattedData = data.map((complaint) => ({
          id: complaint.id,

          date: FormatDateTimeAnneeMoisJour(complaint.dateCreation),

          restaurant: complaint.nomEmplacement || "",

          phoneNumber: complaint.noTel || "",

          personInCharge: complaint.nomResponsable || "",

          product:
            userState.lang === "EN"
              ? [complaint.idItemAccPac, complaint.nomEn, complaint.format]
                  .filter(
                    (value) =>
                      value !== null && value !== undefined && value !== "",
                  )
                  .join(" | ")
              : [complaint.idItemAccPac, complaint.nomFr, complaint.format]
                  .filter(
                    (value) =>
                      value !== null && value !== undefined && value !== "",
                  )
                  .join(" | "),

          problem: complaint.probleme || "",

          quantity: complaint.quantite || "",

          supplier: complaint.fournisseur || "",

          transporteur: complaint.transporteur || "",

          unit: complaint.unit || "",

          status:
            userState.lang === "EN"
              ? complaint.statutEn || ""
              : complaint.statutFr || "",
        }));

        // ======================================================
        // UPDATE GRID
        // ======================================================

        setComplaints(formattedData);

        // ======================================================
        // UPDATE SELECTED COMPLAINT
        // ======================================================

        setSelectedComplaint((previous) => {
          if (!previous) {
            return null;
          }

          const updatedComplaint = formattedData.find(
            (complaint) => complaint.id === previous.id,
          );

          return updatedComplaint || null;
        });
      } catch (error) {
        console.error("Error fetching complaints:", error);

        if (!isMounted) {
          return;
        }

        setAlertMessage(`${t("UnableToFetchComplaints")}: ${error.message}`);

        setAlertType("error");

        setAlertOpen(true);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (userState.token) {
      fetchComplaints();
    }

    return () => {
      isMounted = false;
    };
  }, [userState.token, userState.lang, refreshComplaints, t]);

  // ============================================================
  // COMPLAINT ADDED
  // ============================================================

  const handleComplaintAdded = () => {
    setOpenAddComplaint(false);

    setSelectedComplaint(null);

    setRefreshComplaints((previous) => previous + 1);
  };

  // ============================================================
  // STATUS HELPERS
  // ============================================================

  const getStatusType = (status) => {
    if (!status) {
      return "default";
    }

    const value = status.toLowerCase();

    // Received
    if (
      value.includes("received") ||
      value.includes("reçu") ||
      value.includes("reçue") ||
      value.includes("recue")
    ) {
      return "received";
    }

    // In Progress
    if (
      value.includes("progress") ||
      value.includes("traitement") ||
      value.includes("process")
    ) {
      return "progress";
    }

    // Credit Rejected
    if (
      value.includes("credit rejected") ||
      value.includes("crédit refusé") ||
      value.includes("credit refuse") ||
      value.includes("crédit refusé")
    ) {
      return "creditRejected";
    }

    // Credit Granted
    if (
      value.includes("credit granted") ||
      value.includes("crédit accordé") ||
      value.includes("credit accorde") ||
      value.includes("crédit accordé")
    ) {
      return "creditGranted";
    }

    // Closed
    if (
      value.includes("closed") ||
      value.includes("fermé") ||
      value.includes("fermee") ||
      value.includes("fermée")
    ) {
      return "closed";
    }

    return "default";
  };

  const getStatusColor = (status) => {
    const type = getStatusType(status);

    switch (type) {
      case "received":
        return "info";

      case "progress":
        return "warning";

      case "creditRejected":
        return "error";

      case "creditGranted":
        return "success";

      case "closed":
        return "success";

      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    const type = getStatusType(status);

    switch (type) {
      case "received":
        return <AssignmentIcon fontSize="small" />;

      case "progress":
        return <PendingIcon fontSize="small" />;

      case "creditRejected":
        return <AssignmentIcon fontSize="small" />;

      case "creditGranted":
        return <CheckCircleIcon fontSize="small" />;

      case "closed":
        return <CheckCircleIcon fontSize="small" />;

      default:
        return null;
    }
  };

  // ============================================================
  // STATISTICS
  // ============================================================

  const statistics = useMemo(() => {
    const total = complaints.length;

    const received = complaints.filter(
      (complaint) => getStatusType(complaint.status) === "received",
    ).length;

    const progress = complaints.filter(
      (complaint) => getStatusType(complaint.status) === "progress",
    ).length;

    const creditRejected = complaints.filter(
      (complaint) => getStatusType(complaint.status) === "creditRejected",
    ).length;

    const creditGranted = complaints.filter(
      (complaint) => getStatusType(complaint.status) === "creditGranted",
    ).length;

    const closed = complaints.filter(
      (complaint) => getStatusType(complaint.status) === "closed",
    ).length;

    return {
      total,
      received,
      progress,
      creditRejected,
      creditGranted,
      closed,
    };
  }, [complaints]);

  // ============================================================
  // FILTERED DATA
  // ============================================================

  const filteredComplaints = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return complaints.filter((complaint) => {
      const matchesSearch =
        !search ||
        Object.values(complaint).some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(search),
        );

      const matchesStatus =
        statusFilter === "ALL" ||
        getStatusType(complaint.status) === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [complaints, searchText, statusFilter]);

  // ============================================================
  // COLUMNS
  // ============================================================
  const columns = useMemo(() => {
    const allColumns = [
      {
        field: "id",
        headerName: "ID",
        width: 80,
      },

      {
        field: "date",
        headerName: t("Date"),
        width: 130,
      },

      {
        field: "restaurant",
        headerName: t("Restaurant"),
        minWidth: 180,
        flex: 1,
      },

      {
        field: "product",
        headerName: t("Product"),
        minWidth: 280,
        flex: 1.5,
      },

      {
        field: "problem",
        headerName: t("Problem"),
        minWidth: 200,
        flex: 1,
      },

      {
        field: "quantity",
        headerName: t("Quantity"),
        width: 80,
      },

      {
        field: "unit",
        headerName: t("Unit"),
        width: 90,
      },

      {
        field: "status",
        headerName: t("Status"),
        width: 160,

        renderCell: (params) => (
          <Chip
            icon={getStatusIcon(params.value)}
            label={params.value || t("NA")}
            color={getStatusColor(params.value)}
            size="small"
            variant="outlined"
          />
        ),
      },

      // ========================================================
      // ACTIONS
      // ========================================================
      ...(canModify
        ? [
            {
              field: "actions",
              headerName: t("Actions"),
              width: 70,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              align: "center",
              headerAlign: "center",

              renderCell: (params) => (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenDeleteDialog(params.row);
                  }}
                  sx={{
                    color: "error.main",
                    "&:hover": {
                      color: "#b71c1c",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              ),
            },
          ]
        : []),
    ];

    return allColumns;
  }, [userState.roles, userState.lang, t, canModify]);

  // ============================================================
  // ROW CLICK
  // ============================================================

  const handleRowClick = (params) => {
    setSelectedComplaint(params.row);
  };

  // ============================================================
  // MODIFY
  // ============================================================

  const handleModify = () => {
    if (!selectedComplaint) {
      return;
    }

    setOpenModifyComplaint(true);
  };

  // ============================================================
  // DELETE DIALOG
  // ============================================================

  const handleOpenDeleteDialog = (complaint) => {
    setComplaintToDelete(complaint);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setComplaintToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!complaintToDelete) {
      return;
    }

    try {
      setLoading(true);

      await DeleteComplaintById(
        userState.token,
        complaintToDelete.id,
        userState.lang,
      );

      // ============================================
      // FERMER LE DIALOG
      // ============================================

      handleCloseDeleteDialog();

      // ============================================
      // DÉSÉLECTIONNER LA PLAINTE
      // ============================================

      setSelectedComplaint(null);

      // ============================================
      // SNACKBAR SUCCESS
      // ============================================

      setAlertMessage(
        userState.lang === "EN"
          ? "The complaint was deleted successfully."
          : "La plainte a été supprimée avec succès.",
      );

      setAlertType("success");
      setAlertOpen(true);

      // ============================================
      // REFRESH GRID
      // ============================================

      setRefreshComplaints((previous) => previous + 1);
    } catch (error) {
      console.error("Error deleting complaint:", error);

      // ============================================
      // SNACKBAR ERROR
      // ============================================

      setAlertMessage(error.message);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RESET FILTERS
  // ============================================================

  const handleResetFilters = () => {
    setSearchText("");

    setStatusFilter("ALL");
  };

  // ============================================================
  // STAT CARD
  // ============================================================

  const StatCard = ({ title, value, icon, active, onClick }) => (
    <Card
      onClick={onClick}
      sx={{
        height: 78,

        cursor: onClick ? "pointer" : "default",

        border: "1px solid",

        borderColor: active ? "primary.main" : "divider",

        borderRadius: 1.5,

        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",

        transition: "all 0.2s ease",

        "&:hover": onClick
          ? {
              transform: "translateY(-1px)",

              boxShadow: "0 3px 8px rgba(0,0,0,0.10)",
            }
          : {},
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          p: "12px 16px !important",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            height: "100%",
          }}
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              sx={{
                fontSize: "0.75rem",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                mt: 0.5,
                lineHeight: 1,
              }}
            >
              {value}
            </Typography>
          </Box>

          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              bgcolor: "action.hover",

              "& svg": {
                fontSize: 20,
              },
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  // ============================================================
  // DETAIL FIELD
  // ============================================================

  const DetailField = ({ label, value }) => (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",

        bgcolor: "#f7f8fa",

        p: {
          xs: 2,
          md: 3,
        },
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Box
        sx={{
          mb: 3,

          display: "flex",

          justifyContent: "space-between",

          alignItems: {
            xs: "flex-start",
            md: "center",
          },

          flexDirection: {
            xs: "column",
            md: "row",
          },

          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            {t("Complaints")}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
            }}
          >
            {t("ManageMonitorFollowUp")}
          </Typography>
        </Box>

        {canModify && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddComplaint(true)}
            sx={{
              minWidth: 180,
              height: 44,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("AddComplaint")}
          </Button>
        )}
      </Box>

      {/* ======================================================
    STATISTICS
====================================================== */}

      <Grid
        container
        spacing={2}
        sx={{
          mb: 3,
        }}
      >
        {/* TOTAL */}
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title={t("TotalComplaints")}
            value={statistics.total}
            icon={<AssignmentIcon color="primary" />}
            active={statusFilter === "ALL"}
            onClick={() => setStatusFilter("ALL")}
          />
        </Grid>

        {/* RECEIVED */}
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title={t("Received")}
            value={statistics.received}
            icon={<AssignmentIcon color="info" />}
            active={statusFilter === "received"}
            onClick={() => setStatusFilter("received")}
          />
        </Grid>

        {/* IN PROGRESS */}
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title={t("InProgress")}
            value={statistics.progress}
            icon={<PendingIcon color="warning" />}
            active={statusFilter === "progress"}
            onClick={() => setStatusFilter("progress")}
          />
        </Grid>

        {/* CREDIT REJECTED */}
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title={t("CreditRejected")}
            value={statistics.creditRejected}
            icon={<AssignmentIcon color="error" />}
            active={statusFilter === "creditRejected"}
            onClick={() => setStatusFilter("creditRejected")}
          />
        </Grid>

        {/* CREDIT GRANTED */}
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title={t("CreditGranted")}
            value={statistics.creditGranted}
            icon={<CheckCircleIcon color="success" />}
            active={statusFilter === "creditGranted"}
            onClick={() => setStatusFilter("creditGranted")}
          />
        </Grid>

        {/* CLOSED */}
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            title={t("Closed")}
            value={statistics.closed}
            icon={<TaskAltIcon color="success" />}
            active={statusFilter === "closed"}
            onClick={() => setStatusFilter("closed")}
          />
        </Grid>
      </Grid>
      {/* ======================================================
          MAIN CONTENT
      ====================================================== */}

      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* ====================================================
            TOOLBAR
        ==================================================== */}

        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder={t("SearchComplaints")}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            sx={{
              minWidth: {
                xs: "100%",
                md: 320,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl
            size="small"
            sx={{
              minWidth: 180,
            }}
          >
            <InputLabel>{t("Status")}</InputLabel>

            <Select
              value={statusFilter}
              label={t("Status")}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <MenuItem value="ALL">{t("AllStatuses")}</MenuItem>
              <MenuItem value="received">{t("Received")}</MenuItem>
              <MenuItem value="progress">{t("InProgress")}</MenuItem>
              <MenuItem value="creditRejected">{t("CreditRejected")}</MenuItem>
              <MenuItem value="creditGranted">{t("CreditGranted")}</MenuItem>
              <MenuItem value="closed">{t("Closed")}</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={handleResetFilters}
            sx={{
              height: 40,
              textTransform: "none",
            }}
          >
            {t("ResetFilters")}
          </Button>

          <Box
            sx={{
              flexGrow: 1,
            }}
          />

          {selectedComplaint && canModify && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleModify}
              sx={{
                height: 40,
                textTransform: "none",
              }}
            >
              {t("ModifyComplaint")}
            </Button>
          )}
        </Box>

        <Divider />

        {/* ====================================================
            CONTENT
        ==================================================== */}

        <Box
          sx={{
            display: "flex",
            minHeight: 600,
          }}
        >
          {/* ==================================================
              GRID
          ================================================== */}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: 2,
            }}
          >
            <DataGridPro
              rows={filteredComplaints}
              columns={columns}
              loading={loading}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick={false}
              rowSelectionModel={
                selectedComplaint ? [selectedComplaint.id] : []
              }
              pagination
              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              slots={{
                toolbar: GridToolbar,

                loadingOverlay: () => <CustomLoadingOverlay t={t} />,

                noRowsOverlay: () => <CustomNoRowsOverlay t={t} />,
              }}
              sx={{
                border: "none",

                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "background.default",

                  borderBottom: "1px solid",

                  borderColor: "divider",
                },

                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },

                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },

                "& .MuiDataGrid-row:hover": {
                  bgcolor: "action.hover",
                },

                "& .MuiDataGrid-cell": {
                  borderColor: "divider",
                },

                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid",

                  borderColor: "divider",
                },
              }}
            />
          </Box>

          {/* ==================================================
              DETAILS PANEL
          ================================================== */}

          <Box
            sx={{
              width: {
                xs: 0,
                lg: 340,
              },

              display: {
                xs: "none",
                lg: "block",
              },

              borderLeft: "1px solid",

              borderColor: "divider",

              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                p: 2.5,
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                {t("ComplaintDetails")}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                }}
              >
                {t("SelectComplaintToViewDetails")}
              </Typography>
            </Box>

            <Divider />

            {selectedComplaint ? (
              <Box
                sx={{
                  p: 2.5,
                }}
              >
                {/* ID / STATUS */}

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    mb: 3,
                  }}
                >
                  <Typography variant="h5" fontWeight={700}>
                    #{selectedComplaint.id}
                  </Typography>

                  <Chip
                    icon={getStatusIcon(selectedComplaint.status)}
                    label={selectedComplaint.status || t("NA")}
                    color={getStatusColor(selectedComplaint.status)}
                    size="small"
                  />
                </Stack>

                {/* GENERAL */}

                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 2,
                  }}
                >
                  {t("GeneralInformation")}
                </Typography>

                <Stack spacing={2}>
                  <DetailField
                    label={t("Date")}
                    value={selectedComplaint.date}
                  />

                  <DetailField
                    label={t("Restaurant")}
                    value={selectedComplaint.restaurant}
                  />

                  <DetailField
                    label={t("PhoneNumber")}
                    value={selectedComplaint.phoneNumber}
                  />

                  <DetailField
                    label={t("PersonInCharge")}
                    value={selectedComplaint.personInCharge}
                  />
                </Stack>

                <Divider
                  sx={{
                    my: 3,
                  }}
                />

                {/* PRODUCT */}

                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 2,
                  }}
                >
                  {t("ProductInformation")}
                </Typography>

                <Stack spacing={2}>
                  <DetailField
                    label={t("Product")}
                    value={selectedComplaint.product}
                  />

                  <DetailField
                    label={t("Quantity")}
                    value={`${selectedComplaint.quantity || "—"} ${
                      selectedComplaint.unit || ""
                    }`}
                  />

                  <DetailField
                    label={t("Supplier")}
                    value={selectedComplaint.supplier}
                  />

                  <DetailField
                    label={t("Transporteur")}
                    value={selectedComplaint.transporteur}
                  />
                </Stack>

                <Divider
                  sx={{
                    my: 3,
                  }}
                />

                {/* PROBLEM */}

                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 2,
                  }}
                >
                  {t("Problem")}
                </Typography>

                <Box
                  sx={{
                    p: 1.5,

                    bgcolor: "action.hover",

                    borderRadius: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedComplaint.problem || t("NoDescriptionAvailable")}
                  </Typography>
                </Box>

                {/* MODIFY */}

                {canModify && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleModify}
                    sx={{
                      mt: 3,
                      height: 44,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {t("ModifyComplaint")}
                  </Button>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <AssignmentIcon
                  sx={{
                    fontSize: 48,
                    opacity: 0.35,
                    mb: 1,
                  }}
                />

                <Typography variant="body2">
                  {t("SelectComplaintFromTable")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Card>

      {/* ======================================================
          ADD COMPLAINT
      ====================================================== */}

      {openAddComplaint && (
        <AddComplaint
          open={openAddComplaint}
          onClose={() => setOpenAddComplaint(false)}
          onComplaintAdded={handleComplaintAdded}
        />
      )}

      {/* ======================================================
          MODIFY COMPLAINT
      ====================================================== */}

      {openModifyComplaint && (
        <ModifyComplaint
          open={openModifyComplaint}
          onClose={() => setOpenModifyComplaint(false)}
          complaintData={selectedComplaint}
          setAlertOpen={setAlertOpen}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
        />
      )}

      {/* ======================================================
    DELETE CONFIRMATION DIALOG
====================================================== */}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 700,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "error.lighter",
                color: "error.main",
              }}
            >
              <WarningIcon />
            </Box>

            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.2 }}
              >
                {t("DeleteComplaint")}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {complaintToDelete ? `#${complaintToDelete.id}` : ""}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("DeleteComplaintConfirmation")}
          </Typography>

          {complaintToDelete && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {complaintToDelete.product || t("NA")}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                {complaintToDelete.restaurant || ""}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1,
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("Cancel")}
          </Button>

          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              minWidth: 110,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",

              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================
          ALERT
      ====================================================== */}

      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertType}
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ComplainAdmin;
